import { useEffect, useMemo, useRef, useState } from "react";
import { useGetQuotationQuery } from "../../sales/quotations/quotationsApi";
import { useLazyGetProductsQuery } from "../../masters/products/productsApi";

const num = (v) => Number(v) || 0;

/**
 * Checks an LPO's lines against the product groups on its linked quotation
 * (Batch185) and returns row flags for LineItemsEditor to paint:
 *
 *   danger  - the product's group is not on the quotation at all
 *   warning - the group is quoted, but the LPO orders more of it than was quoted
 *
 * A warning, never a block: cable, connectors and the like are bought for a job
 * without ever being quoted line by line. The point is that whoever raises and
 * whoever approves the LPO can SEE the difference, not that the form refuses it.
 *
 * Deliberately silent (returns null flags) when the quotation names no group at
 * all - every quotation written before group tags existed is pure free text, and
 * flagging every line of every such LPO red would teach people to ignore the colour.
 *
 * Group ids are branch-local. That is safe here because a quotation line's group
 * is resolved against the quotation's own branch, and the LPO's products are
 * searched in the LPO's branch - the check only runs when the two are the same.
 */
export const useQuotationGroupCheck = ({ quotationRecordGuid, branchId, rows }) => {
  const { data: quotation } = useGetQuotationQuery(quotationRecordGuid, { skip: !quotationRecordGuid });
  const [searchProducts] = useLazyGetProductsQuery();

  // A row picked in this session carries its product's group already. A row
  // loaded from a saved LPO does not (the LPO line stores no group), so those
  // are looked up once per product code and remembered.
  const [resolved, setResolved] = useState({});
  const inFlight = useRef(new Set());

  const quotedGroups = useMemo(() => {
    const map = new Map();
    if (!quotation || Number(quotation.branchId) !== Number(branchId)) return map;
    (quotation.lines || []).forEach((l) => {
      if (l.productGroupId == null) return;
      const key = String(l.productGroupId);
      const entry = map.get(key) || { name: l.productGroupName || `Group ${key}`, qty: 0 };
      entry.qty += num(l.qty);
      map.set(key, entry);
    });
    return map;
  }, [quotation, branchId]);

  const isActive = quotedGroups.size > 0;

  useEffect(() => {
    if (!isActive || !branchId) return;
    rows.forEach((row) => {
      const code = row.productCode ? String(row.productCode) : "";
      if (!code || row.productGroupId || code in resolved || inFlight.current.has(code)) return;
      inFlight.current.add(code);
      searchProducts({ branchId: Number(branchId), search: code }, true)
        .unwrap()
        .then((matches) => {
          const product = (matches || []).find((p) => String(p.productCode) === code);
          setResolved((prev) => ({
            ...prev,
            [code]: { groupId: product?.productGroupId ?? null, groupName: product?.productGroupName ?? null },
          }));
        })
        .catch(() => {})
        .finally(() => inFlight.current.delete(code));
    });
  }, [rows, isActive, branchId, resolved, searchProducts]);

  return useMemo(() => {
    if (!isActive) return { rowFlags: null, summary: null, quotedGroups };

    const groupOf = (row) => {
      if (row.productGroupId) return { groupId: row.productGroupId, groupName: row.productGroupName };
      return resolved[String(row.productCode)];
    };

    const orderedByGroup = new Map();
    const rowFlags = {};
    let outside = 0;

    rows.forEach((row) => {
      if (!row.productCode) return;
      const group = groupOf(row);
      if (!group) return; // still being looked up - say nothing rather than guess
      const key = group.groupId != null ? String(group.groupId) : null;
      if (!key || !quotedGroups.has(key)) {
        outside += 1;
        rowFlags[row.key] = {
          tone: "danger",
          text: key ? `Not on the quotation - group: ${group.groupName || key}` : "Not on the quotation - product has no group",
        };
        return;
      }
      orderedByGroup.set(key, (orderedByGroup.get(key) || 0) + num(row.qty));
    });

    let over = 0;
    rows.forEach((row) => {
      if (!row.productCode || rowFlags[row.key]) return;
      const group = groupOf(row);
      const key = group?.groupId != null ? String(group.groupId) : null;
      if (!key) return;
      const quoted = quotedGroups.get(key);
      const ordered = orderedByGroup.get(key) || 0;
      if (quoted && ordered > quoted.qty) {
        over += 1;
        rowFlags[row.key] = { tone: "warning", text: `Over the quotation - ${quoted.name}: ordering ${ordered}, quoted ${quoted.qty}` };
      }
    });

    return { rowFlags, summary: { outside, over }, quotedGroups };
  }, [isActive, rows, resolved, quotedGroups]);
};
